<template>
  <div class="roles">
    <div class="default-table">
      <div class="pt-1 pb-1 pl-1">
        <b-button
          v-if="$can('create', 'roles', 'captain')"
          variant="bg-primary"
          @click="$router.push({ name: 'create-role' })"
        >
          <GjIcon
            class="icon-left"
            name="Plus"
          />
          Create new Role
        </b-button>
      </div>
      <div class="position-relative d-none d-lg-flex h-100">
        <b-table
          ref="table"
          :items="roles"
          :fields="fields"
          :class="{ loaded: !loading }"
          show-empty
          responsive
        >
          <template #empty>
            <div
              v-if="loading"
              class="loading-table"
            />
            <no-data
              v-else
              no-data-text="No roles have been added yet..."
              create-text="+ Create Role"
              @action="$router.push('create-role')"
            />
          </template>

          <template #cell(isDefault)="data">
            <template v-if="data.item.isDefault">
              System
            </template>
            <template v-else>
              <a
                v-if="$can('update', 'roles', 'captain')"
                @click="editRole(data.item.id)"
              >
                <GjIcon
                  class="icon-right"
                  name="Edit"
                />
              </a>
              <template v-if="$can('delete', 'roles', 'captain')">
                <b-spinner
                  v-if="removingRoleId == data.item.id && removingRole"
                  variant="primary"
                  small
                  class="icon-right"
                />
                <a
                  v-else
                  @click="deleteRole(data.item.id)"
                >
                  <GjIcon
                    class="icon-right"
                    name="Delete"
                  />
                </a>
              </template>
            </template>
          </template>
        </b-table>
        <table-busy v-if="loading && roles.length > 0" />
      </div>
      <div class="position-relative d-lg-none px-1">
        <template v-for="role in roles">
          <card
            :key="role.id"
            :fields="fields"
            :item="role"
            class="shadow"
          >
            <template #options>
              <b-dropdown
                v-if="!role.isDefault"
                id="dropdown-1"
                right
                variant="white"
              >
                <template #button-content>
                  <GjIcon name="Moreoptions" />
                </template>
                <b-dropdown-item
                  v-if="$can('update', 'roles', 'captain')"
                  @click="editRole(role.id)"
                >
                  Edit
                </b-dropdown-item>
                <b-dropdown-item
                  v-if="$can('delete', 'roles', 'captain')"
                  @click="deleteRole(role.id)"
                >Delete</b-dropdown-item>
              </b-dropdown>
            </template>
          </card>
        </template>
        <div
          v-if="loading"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && roles.length < 1">
          <no-data
            class="text-center"
            no-data-text="No roles have been added yet..."
            create-text="+ Create Role"
            @action="$router.push('create-role')"
          />
        </template>
      </div>
      <div
        v-if="roles.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'

export default {
  name: 'Roles',
  components: { Card },
  data() {
    return {
      fields: [
        { key: 'name', isPrimary: true },
        { key: 'description', isPrimary: true },
        'id',
        { key: 'permissionCount', label: 'Permissions' },
        { key: 'isDefault', label: 'Actions', hide: true },
      ],
      removingRoleId: null,
    }
  },
  computed: {
    roles() {
      return this.$store.state.roles.roles
    },
    resultInfo() {
      return this.$store.state.roles.resultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    loading() {
      return this.$store.state.roles.fetchingRoles
    },
    removingRole() {
      return this.$store.state.roles.removingRole
    },
    currentPage: {
      get() {
        return this.$store.getters['roles/currentPage']
      },
      set(val) {
        this.$store.commit('roles/SET_CURRENT_PAGE', val)
      },
    },
  },
  watch: {
    currentPage() {
      this.fetchRoles()
    },
  },
  created() {
    if (this.$store.state.app.previousRouteName !== 'edit-role') {
      this.currentPage = 1
    }
    this.fetchRoles()
  },
  methods: {
    fetchRoles() {
      const query = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
      }
      this.$store.dispatch('roles/fetchRoles', query).catch(error => {
        this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while fetching roles.')
      })
    },
    deleteRole(id) {
      this.confirmDelete(
        'Delete Role',
        'Are you sure you want to delete this role?',
      ).then(ok => {
        if (ok) {
          this.removingRoleId = id
          this.$store
            .dispatch('roles/deleteRole', id)
            .then(() => {
              this.removingRoleId = null
              this.successToast('Role was deleted')
              if (this.roles.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.fetchRoles()
              }
            })
            .catch(error => {
              this.removingRoleId = null
              this.errorToast(error.response?.data?.errors[0] || 'Something went wrong while deleting role.')
            })
        }
      })
    },
    editRole(id) {
      this.$router.push({ name: 'edit-role', params: { id } })
    },
  },
}
</script>
<style lang="scss">
.roles {
  height: 100%;

  .default-table {
    margin-top: 0;
    box-shadow: none;
    height: 100%;
    display: flex;
    flex-direction: column;
    .table-responsive {
      height: 100%;
      overflow: auto;
      display: flex;
      flex-direction: column;
      table {
        border-bottom: 0;
        margin-top: 0;
        thead {
          th:last-child {
            width: 30px;
          }
        }
      }
    }
  }
}
</style>
